// Accident Metrics Dashboard — the main client-ready visual reporting page.
import { useEffect, useState } from "react";
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar, ComposedChart, Line,
    PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
    Activity, ShieldAlert, Clock, HeartPulse, Stethoscope, ListChecks,
    AlertTriangle, CalendarCheck, Bandage,
} from "lucide-react";
import { api } from "@/lib/api";
import {
    FilterBar, buildParams, KpiCard, ChartCard, Empty, PALETTE, CATEGORY_COLORS, TT,
} from "@/pages/safety/metrics/shared";

const CAT_ORDER = ["No Treatment", "First Aid", "Medical Aid", "Lost Time", "Lost Time RIDDOR", "Fatality"];

export default function AccidentDashboard({ filters, onChange, clients, buildings, projects, meta, isPortal }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        setData(null); setError(false);
        api.get("/incidents/dashboard", { params: buildParams(filters) })
            .then((r) => setData(r.data)).catch(() => setError(true));
    }, [filters]);

    return (
        <div data-testid="accident-dashboard" className="space-y-5">
            <FilterBar filters={filters} onChange={onChange} clients={clients} buildings={buildings}
                projects={projects} meta={meta} isPortal={isPortal} />

            {error ? (
                <div className="text-[#9A968F] italic py-12 text-center">Couldn't load the accident metrics.</div>
            ) : !data ? (
                <div className="text-[#9A968F] italic py-12 text-center">Loading metrics…</div>
            ) : (<>
                {/* KPI cards */}
                <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))" }}>
                    <KpiCard testid="kpi-total" icon={Activity} tone="accent" label="Accidents / incidents" value={data.totals.total} />
                    <KpiCard testid="kpi-riddor" icon={ShieldAlert} tone="red" label="RIDDOR reportable" value={data.totals.riddor} />
                    <KpiCard testid="kpi-losttime" icon={Clock} tone="amber" label="Lost-time incidents" value={data.totals.lost_time} />
                    <KpiCard testid="kpi-firstaid" icon={Bandage} tone="green" label="First-aid injuries" value={data.totals.first_aid} />
                    <KpiCard testid="kpi-notreatment" icon={HeartPulse} tone="neutral" label="No-treatment" value={data.totals.no_treatment} />
                    <KpiCard testid="kpi-medical" icon={Stethoscope} tone="amber" label="Medical-aid" value={data.totals.medical_aid} />
                    <KpiCard testid="kpi-dayslost" icon={Clock} tone="neutral" label="Days lost" value={data.totals.days_lost} sub={`avg ${data.totals.avg_days_lost}/incident`} />
                    <KpiCard testid="kpi-openactions" icon={ListChecks} tone="blue" label="Open actions" value={data.totals.open_actions} />
                    <KpiCard testid="kpi-overdueactions" icon={AlertTriangle} tone="red" label="Overdue actions" value={data.totals.overdue_actions} />
                    <KpiCard testid="kpi-streak" icon={CalendarCheck} tone="green" label="Safe-day streak" value={data.totals.safe_day_streak} sub="days incident-free" />
                </div>

                {/* A. Trend + B. Rate vs target */}
                <div className="grid lg:grid-cols-2 gap-5">
                    <ChartCard testid="chart-trend" title="Accident trend over time" subtitle="Monthly accident / incident totals">
                        <ResponsiveContainer width="100%" height={250}>
                            <AreaChart data={data.trend} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#C75A1B" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="#C75A1B" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={TT} />
                                <Area type="monotone" dataKey="total" name="Incidents" stroke="#C75A1B" strokeWidth={2.5} fill="url(#trendFill)" dot={{ r: 2.5 }} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard testid="chart-rate" title="Accident rate vs target"
                        subtitle={data.rate.has_data ? `${data.rate.basis} per 100,000 ${data.rate.basis === "AIR" ? "FTE" : "hours"} · target line` : "Configure hours worked / FTE"}>
                        {!data.rate.has_data ? (
                            <Empty label="Missing hours/FTE data — set annual hours worked or FTE in Safety Cross → Settings to compute the rate." />
                        ) : (
                            <ResponsiveContainer width="100%" height={250}>
                                <ComposedChart data={data.rate.monthly} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                                    <Tooltip contentStyle={TT} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Bar dataKey="rate" name={data.rate.basis} fill="#2A5DB0" radius={[3, 3, 0, 0]} />
                                    <Line type="monotone" dataKey="target" name="Target" stroke="#B33A2E" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>

                    {/* C. Categories stacked */}
                    <ChartCard testid="chart-categories" title="Accident categories by month" subtitle="Stacked by outcome category">
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={data.categories_monthly} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip contentStyle={TT} />
                                <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                                {CAT_ORDER.map((c) => (
                                    <Bar key={c} dataKey={c} name={c} stackId="cat" fill={CATEGORY_COLORS[c]} radius={[2, 2, 0, 0]} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* D. Category share donut */}
                    <ChartCard testid="chart-category-share" title="Category share" subtitle="Percentage split by accident category">
                        {data.category_share.length === 0 ? <Empty /> : (
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie data={data.category_share} dataKey="count" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}
                                        label={(e) => `${Math.round((e.count / data.totals.total) * 100)}%`} labelLine={false}>
                                        {data.category_share.map((s) => <Cell key={s.name} fill={CATEGORY_COLORS[s.name] || "#9A968F"} />)}
                                    </Pie>
                                    <Tooltip contentStyle={TT} />
                                    <Legend wrapperStyle={{ fontSize: 10.5 }} iconSize={8} />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </div>

                {/* E. Root cause Pareto + F. Initial cause + G. Injury type + H. Unsafe split */}
                <div className="grid lg:grid-cols-2 gap-5">
                    <ChartCard testid="chart-root-cause" title="Root cause analysis" subtitle="Pareto — count + cumulative %">
                        <ParetoChart rows={data.root_causes} />
                    </ChartCard>

                    <ChartCard testid="chart-initial-cause" title="Initial cause analysis" subtitle="Mechanism of incident (top causes)">
                        <HBar rows={data.initial_causes} color="#2A5DB0" />
                    </ChartCard>

                    <ChartCard testid="chart-injury-type" title="Type of injury" subtitle="Top injury types (others grouped)">
                        <HBar rows={data.injury_types} color="#B33A2E" />
                    </ChartCard>

                    <ChartCard testid="chart-unsafe-split" title="Unsafe act vs unsafe condition">
                        <UnsafeDonut split={data.unsafe_split} />
                    </ChartCard>
                </div>

                {/* I. Site / building league */}
                <SiteLeague league={data.league} />
            </>)}
        </div>
    );
}

function ParetoChart({ rows }) {
    if (!rows || rows.length === 0) return <Empty />;
    const top = rows.slice(0, 10);
    const total = top.reduce((s, r) => s + r.count, 0) || 1;
    let cum = 0;
    const data = top.map((r) => { cum += r.count; return { name: r.name, count: r.count, cum: Math.round((cum / total) * 100) }; });
    return (
        <ResponsiveContainer width="100%" height={270}>
            <ComposedChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 56 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" />
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#6F6A64" }} interval={0} angle={-32} textAnchor="end" height={70} axisLine={false} tickLine={false} />
                <YAxis yAxisId="l" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "#9A968F" }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" />
                <Tooltip contentStyle={TT} />
                <Bar yAxisId="l" dataKey="count" name="Incidents" fill="#C75A1B" radius={[3, 3, 0, 0]} />
                <Line yAxisId="r" type="monotone" dataKey="cum" name="Cumulative %" stroke="#1F1F1F" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
        </ResponsiveContainer>
    );
}

function HBar({ rows, color }) {
    if (!rows || rows.length === 0) return <Empty />;
    const data = rows.slice(0, 10);
    return (
        <ResponsiveContainer width="100%" height={Math.max(210, data.length * 26)}>
            <BarChart layout="vertical" data={data} margin={{ top: 4, right: 20, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0EDE8" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#6F6A64" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10.5, fill: "#6F6A64" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TT} cursor={{ fill: "#FBEFE5" }} />
                <Bar dataKey="count" name="Incidents" fill={color} radius={[0, 3, 3, 0]} label={{ position: "right", fontSize: 10, fill: "#6F6A64" }} />
            </BarChart>
        </ResponsiveContainer>
    );
}

function UnsafeDonut({ split }) {
    const data = Object.entries(split || {}).map(([name, value]) => ({ name, value })).filter((d) => d.value > 0);
    const colors = { "Unsafe act": "#C75A1B", "Unsafe condition": "#2A5DB0", "Both": "#6B4E9E", "Neither": "#9A968F" };
    if (data.length === 0) return <Empty />;
    return (
        <ResponsiveContainer width="100%" height={250}>
            <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {data.map((d) => <Cell key={d.name} fill={colors[d.name] || "#9A968F"} />)}
                </Pie>
                <Tooltip contentStyle={TT} />
                <Legend wrapperStyle={{ fontSize: 10.5 }} iconSize={8} />
            </PieChart>
        </ResponsiveContainer>
    );
}

function SiteLeague({ league }) {
    if (!league || league.length === 0) return null;
    const max = Math.max(...league.map((l) => l.total), 1);
    return (
        <div data-testid="chart-site-league" className="bg-white border border-[#E5E0DA] rounded-[11px] overflow-hidden">
            <div className="px-5 py-3 border-b border-[#F0EDE8]"><h3 className="text-[13.5px] font-semibold text-[#1F1F1F]">Site / building comparison</h3></div>
            <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                    <thead><tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider text-[#6F6A64]">
                        <th className="text-left px-5 py-2.5 font-bold">Site / building</th>
                        <th className="text-right px-3 py-2.5 font-bold">Total</th>
                        <th className="text-right px-3 py-2.5 font-bold">RIDDOR</th>
                        <th className="text-right px-3 py-2.5 font-bold">Lost time</th>
                        <th className="text-right px-3 py-2.5 font-bold">Days lost</th>
                        <th className="text-right px-3 py-2.5 font-bold">Open actions</th>
                        <th className="text-right px-5 py-2.5 font-bold">Overdue</th>
                    </tr></thead>
                    <tbody>
                        {league.map((l) => (
                            <tr key={l.key} data-testid={`league-row-${l.key}`} className="border-t border-[#F0EDE8]">
                                <td className="px-5 py-2.5 font-semibold text-[#1F1F1F]">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block h-1.5 rounded-full" style={{ width: `${Math.max(8, (l.total / max) * 90)}px`, background: "#C75A1B" }} />
                                        {l.name}
                                    </div>
                                </td>
                                <td className="px-3 py-2.5 text-right">{l.total}</td>
                                <td className="px-3 py-2.5 text-right font-semibold" style={{ color: l.riddor ? "#B33A2E" : "#9A968F" }}>{l.riddor}</td>
                                <td className="px-3 py-2.5 text-right">{l.lost_time}</td>
                                <td className="px-3 py-2.5 text-right">{l.days_lost}</td>
                                <td className="px-3 py-2.5 text-right">{l.open_actions}</td>
                                <td className="px-5 py-2.5 text-right font-semibold" style={{ color: l.overdue_actions ? "#B33A2E" : "#9A968F" }}>{l.overdue_actions}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
